/**
 * IndexedDB service using Dexie.js for local storage of translations
 */

import Dexie, { Table } from 'dexie';
import { v4 as uuidv4 } from 'uuid';
import { isFuzzyMatchInList, parseWordList, joinWordList } from './fuzzyMatch';

// Normalize category casing to Title Case at read time, so categories stored with
// inconsistent casing (e.g. from before this normalization existed, or from a mix of
// the single-translate and bulk-recategorize paths) still display and group as one
// category instead of fragmenting into lookalike groups like "fruit" vs "Fruit".
function normalizeCategory(category: string): string {
  return category
    .trim()
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Type definitions
export interface AlternativeTerm {
  term: string;
  pinyin?: string;
  context: string;
}

export interface LocalTranslation {
  id: string;
  original: string;              // English word(s), CSV format for merged entries
  translation: string;           // Chinese translation
  officialTranslation: string;
  alternatives: AlternativeTerm[];
  pinyin: string;
  examples: string[];
  category: string;
  context: string | null;
  sourceLang: 'en' | 'zh';
  targetLang: 'en' | 'zh';
  searchCount: number;
  lastSearched: Date;
  createdAt: Date;
}

export interface TranslationInput {
  original: string;
  cleaned?: string;
  translation: string;
  officialTranslation: string;
  alternatives: AlternativeTerm[];
  pinyin: string;
  examples: string[];
  category: string;
  context?: string | null;
  sourceLang: 'en' | 'zh';
  targetLang: 'en' | 'zh';
}

export interface ExportData {
  version: number;
  exportedAt: string;
  appVersion: string;
  translations: LocalTranslation[];
}

export interface ImportResult {
  imported: number;
  merged: number;
  skipped: number;
  errors: string[];
}

// Dexie database class
class TranslateCardDB extends Dexie {
  translations!: Table<LocalTranslation, string>;

  constructor() {
    super('translatecard-db');

    this.version(1).stores({
      translations: 'id, translation, category, lastSearched, searchCount'
    });
  }
}

// Database instance
const db = new TranslateCardDB();

/**
 * Initialize the database (called on app mount)
 */
export async function initializeDB(): Promise<void> {
  await db.open();
}

/**
 * Get a single translation by ID
 */
export async function getTranslation(id: string): Promise<LocalTranslation | undefined> {
  return db.translations.get(id);
}

/**
 * Get all translations sorted by lastSearched (most recent first)
 */
export async function getAllTranslations(): Promise<LocalTranslation[]> {
  const translations = await db.translations
    .orderBy('lastSearched')
    .reverse()
    .toArray();

  return translations.map(t => ({ ...t, category: normalizeCategory(t.category || 'General') }));
}

/**
 * Find a translation by Chinese text
 */
export async function findByChineseTranslation(
  chinese: string,
  sourceLang: 'en' | 'zh' = 'en',
  targetLang: 'en' | 'zh' = 'zh'
): Promise<LocalTranslation | undefined> {
  return db.translations
    .where('translation')
    .equals(chinese)
    .filter(t => t.sourceLang === sourceLang && t.targetLang === targetLang)
    .first();
}

/**
 * Save or merge a translation with smart deduplication
 * - If Chinese translation exists: merge English words with fuzzy matching, increment count
 * - If new: create new record
 */
export async function saveOrMergeTranslation(input: TranslationInput): Promise<LocalTranslation> {
  const textToStore = input.cleaned || input.original;
  const normalizedContext = input.context ? input.context.trim() || null : null;
  const finalCategory = normalizeCategory(input.category || 'General');

  // Check if Chinese translation already exists
  const existing = await findByChineseTranslation(
    input.translation,
    input.sourceLang,
    input.targetLang
  );

  if (existing) {
    // Merge with existing entry
    const existingWords = parseWordList(existing.original);
    const isFuzzyMatch = isFuzzyMatchInList(textToStore, existingWords);

    const updatedEnglishWords = isFuzzyMatch
      ? existingWords
      : [...existingWords, textToStore];

    const updated: LocalTranslation = {
      ...existing,
      original: joinWordList(updatedEnglishWords),
      officialTranslation: input.officialTranslation,
      alternatives: input.alternatives,
      pinyin: input.pinyin,
      examples: input.examples,
      category: finalCategory,
      searchCount: existing.searchCount + 1,
      lastSearched: new Date()
    };

    await db.translations.put(updated);
    return updated;
  } else {
    // Create new entry
    const newTranslation: LocalTranslation = {
      id: uuidv4(),
      original: textToStore,
      translation: input.translation,
      officialTranslation: input.officialTranslation,
      alternatives: input.alternatives,
      pinyin: input.pinyin,
      examples: input.examples,
      category: finalCategory,
      context: normalizedContext,
      sourceLang: input.sourceLang,
      targetLang: input.targetLang,
      searchCount: 1,
      lastSearched: new Date(),
      createdAt: new Date()
    };

    await db.translations.add(newTranslation);
    return newTranslation;
  }
}

/**
 * Increment search count and update lastSearched timestamp
 */
export async function incrementSearchCount(id: string): Promise<LocalTranslation | undefined> {
  const translation = await db.translations.get(id);
  if (!translation) return undefined;

  const updated: LocalTranslation = {
    ...translation,
    searchCount: translation.searchCount + 1,
    lastSearched: new Date()
  };

  await db.translations.put(updated);
  return updated;
}

/**
 * Delete a translation by ID
 */
export async function deleteTranslation(id: string): Promise<void> {
  await db.translations.delete(id);
}

/**
 * Get translations grouped by category
 */
export async function getCategorizedHistory(): Promise<Record<string, LocalTranslation[]>> {
  const translations = await db.translations
    .filter(t => t.category !== null && t.category !== undefined)
    .toArray();

  // Sort by searchCount descending
  translations.sort((a, b) => b.searchCount - a.searchCount);

  const categorized: Record<string, LocalTranslation[]> = {};

  for (const translation of translations) {
    const category = normalizeCategory(translation.category || 'General');
    if (!categorized[category]) {
      categorized[category] = [];
    }
    categorized[category].push({ ...translation, category });
  }

  return categorized;
}

/**
 * Update categories for multiple translations
 * @param categoryMapping - Map of Chinese translation -> new category
 */
export async function updateAllCategories(
  categoryMapping: Record<string, string>
): Promise<void> {
  const translations = await db.translations.toArray();

  const updates = translations.map(async (translation) => {
    const newCategory = categoryMapping[translation.translation];
    if (newCategory && newCategory !== translation.category) {
      await db.translations.update(translation.id, { category: newCategory });
    }
  });

  await Promise.all(updates);
}

/**
 * Export all data to JSON string
 */
export async function exportToJSON(): Promise<string> {
  const translations = await getAllTranslations();

  const exportData: ExportData = {
    version: 1,
    exportedAt: new Date().toISOString(),
    appVersion: '1.0.0',
    translations
  };

  return JSON.stringify(exportData, null, 2);
}

/**
 * Import data from JSON string
 */
export async function importFromJSON(json: string): Promise<ImportResult> {
  const result: ImportResult = {
    imported: 0,
    merged: 0,
    skipped: 0,
    errors: []
  };

  try {
    const data = JSON.parse(json) as ExportData;

    // Validate structure
    if (!data.translations || !Array.isArray(data.translations)) {
      result.errors.push('Invalid export file: missing translations array');
      return result;
    }

    for (const translation of data.translations) {
      try {
        // Validate required fields
        if (!translation.translation || !translation.original) {
          result.skipped++;
          continue;
        }

        // Check if exists
        const existing = await findByChineseTranslation(
          translation.translation,
          translation.sourceLang || 'en',
          translation.targetLang || 'zh'
        );

        if (existing) {
          // Merge: combine English words, take max searchCount
          const existingWords = parseWordList(existing.original);
          const importWords = parseWordList(translation.original);

          // Add words that don't already exist (with fuzzy matching)
          const newWords = importWords.filter(w => !isFuzzyMatchInList(w, existingWords));
          const mergedWords = [...existingWords, ...newWords];

          await db.translations.update(existing.id, {
            original: joinWordList(mergedWords),
            searchCount: Math.max(existing.searchCount, translation.searchCount || 1),
            // Keep other fields from existing unless import has newer data
            officialTranslation: translation.officialTranslation || existing.officialTranslation,
            alternatives: translation.alternatives?.length ? translation.alternatives : existing.alternatives,
            pinyin: translation.pinyin || existing.pinyin,
            examples: translation.examples?.length ? translation.examples : existing.examples,
            category: translation.category || existing.category
          });

          result.merged++;
        } else {
          // Import as new
          const newTranslation: LocalTranslation = {
            id: uuidv4(), // Generate new ID to avoid conflicts
            original: translation.original,
            translation: translation.translation,
            officialTranslation: translation.officialTranslation || '',
            alternatives: translation.alternatives || [],
            pinyin: translation.pinyin || '',
            examples: translation.examples || [],
            category: translation.category || 'General',
            context: translation.context || null,
            sourceLang: translation.sourceLang || 'en',
            targetLang: translation.targetLang || 'zh',
            searchCount: translation.searchCount || 1,
            lastSearched: translation.lastSearched ? new Date(translation.lastSearched) : new Date(),
            createdAt: translation.createdAt ? new Date(translation.createdAt) : new Date()
          };

          await db.translations.add(newTranslation);
          result.imported++;
        }
      } catch (itemError) {
        result.errors.push(`Failed to import "${translation.original}": ${itemError}`);
        result.skipped++;
      }
    }
  } catch (parseError) {
    result.errors.push(`Failed to parse JSON: ${parseError}`);
  }

  return result;
}

/**
 * Clear all data from the database
 */
export async function clearAllData(): Promise<void> {
  await db.translations.clear();
}

/**
 * Get total count of translations
 */
export async function getTranslationCount(): Promise<number> {
  return db.translations.count();
}

// Export the database instance for advanced usage
export { db };
